import { useEffect, useMemo, useState } from 'react';
import type {} from 'react-select-v5';
import ReactSelect, { GroupBase } from 'react-select-v5';

import { toJsIfImmutable } from 'shared/functions';

import { SelectProps } from './SelectV5Types';
import { findOption, getReactSelectStyles, tryToStringify, valueToOption } from './functions';

export const SelectV5 = <
  OptionType,
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>({
  styles,
  options,
  optionRenderer,
  clearable,
  multi,
  disabled,
  value,
  valueKey,
  labelKey,
  valueRenderer,
  onChange,
  formatOptionLabel,
  isFixedOption,
  filterValue = () => true,
  ...rest
}: SelectProps<OptionType, IsMulti, GroupType>) => {
  const [selectValue, setValue] = useState();

  const jsValue = useMemo(() => toJsIfImmutable(value), [value]);

  const getOptionValue = (option: any) => (valueRenderer ? valueRenderer(option) : option[valueKey]);

  const getOptionLabel = (initialOption: any) => {
    const option = valueToOption(initialOption, options, valueKey);

    return optionRenderer ? optionRenderer(option) : option[labelKey];
  };

  const valueSelect = useMemo(() => {
    if (multi) {
      if (Array.isArray(jsValue)) {
        return jsValue.map(findOption(valueKey, options)).filter(filterValue);
      }
      return jsValue;
    }
    return findOption(valueKey, options)(jsValue);
  }, [filterValue, multi, jsValue, options, valueKey]);

  useEffect(() => {
    if (tryToStringify(selectValue) !== tryToStringify(valueSelect)) {
      setValue(valueSelect);
    }
  }, [selectValue, valueSelect]);

  const selectStyles = getReactSelectStyles(multi, isFixedOption, styles);

  return (
    <ReactSelect
      isClearable={clearable === undefined ? true : clearable}
      isMulti={!!multi}
      isDisabled={disabled}
      menuPlacement="auto"
      className="Select"
      classNamePrefix="Select-control"
      getOptionValue={getOptionValue}
      getOptionLabel={getOptionLabel}
      formatOptionLabel={formatOptionLabel}
      options={options}
      {...rest}
      styles={selectStyles}
      value={selectValue}
      onChange={(updatedValue, triggeredAction) => {
        if (!onChange) {
          return;
        }

        const { action, removedValue } = triggeredAction;

        let returningValue = updatedValue;
        switch (action) {
          case 'remove-value':
          case 'pop-value':
            if (removedValue?.rsIsFixedValue || removedValue?.isFixed) {
              return;
            }
            break;
          case 'clear':
            if (Array.isArray(updatedValue)) {
              returningValue = updatedValue.filter((v) => v.isFixed);
            }
            break;

          default:
            break;
        }

        if (multi && updatedValue === null) {
          returningValue = [];
        }

        onChange(returningValue, triggeredAction);
      }}
    />
  );
};
